import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2, X, Filter, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  BroadcastFilter,
  BroadcastFilterType,
  FILTER_TYPE_LABELS,
  FILTER_TYPE_DESCRIPTIONS,
  toApiFilterType,
} from '@/types/broadcast-filters';
import {
  useAdminGetProducts,
  useAdminAddBroadcastFilter,
  useAdminUpdateBroadcastFilter,
  useAdminDeleteBroadcastFilter,
  getAdminGetBroadcastQueryKey,
} from '@/api/auth/admin/admin';
import { logger } from '@/services/logger';

interface BroadcastFiltersEditorProps {
  broadcastId: string;
  filters: BroadcastFilter[];
  disabled?: boolean;
}

/**
 * Editor component for managing broadcast filters using CRUD API endpoints
 */
export function BroadcastFiltersEditor({
  broadcastId,
  filters,
  disabled = false,
}: BroadcastFiltersEditorProps) {
  const [newFilterType, setNewFilterType] = useState<BroadcastFilterType | ''>('');
  const queryClient = useQueryClient();

  const { data: productsData } = useAdminGetProducts();
  const products = productsData?.data?.products ?? [];

  const invalidateBroadcast = () => {
    queryClient.invalidateQueries({ queryKey: getAdminGetBroadcastQueryKey(broadcastId) });
  };

  const { mutate: addFilter, isPending: isAdding } = useAdminAddBroadcastFilter({
    mutation: {
      onSuccess: () => {
        toast.success('Filter added');
        invalidateBroadcast();
        setNewFilterType('');
      },
      onError: (error: any) => {
        logger.error(error, { context: 'AddBroadcastFilter', broadcastId });
        toast.error('Failed to add filter: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const { mutate: updateFilter, isPending: isUpdating } = useAdminUpdateBroadcastFilter({
    mutation: {
      onSuccess: () => {
        toast.success('Filter updated');
        invalidateBroadcast();
      },
      onError: (error: any) => {
        logger.error(error, { context: 'UpdateBroadcastFilter', broadcastId });
        toast.error('Failed to update filter: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const { mutate: deleteFilter, isPending: isDeleting } = useAdminDeleteBroadcastFilter({
    mutation: {
      onSuccess: () => {
        toast.success('Filter removed');
        invalidateBroadcast();
      },
      onError: (error: any) => {
        logger.error(error, { context: 'DeleteBroadcastFilter', broadcastId });
        toast.error('Failed to remove filter: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const isBusy = isAdding || isUpdating || isDeleting;

  const handleAddFilter = () => {
    if (!newFilterType) return;
    addFilter({
      broadcastId,
      data: {
        filterType: toApiFilterType(newFilterType),
        ids: newFilterType === 'Ids' ? [] : undefined,
        productIds: newFilterType === 'ProductRegistration' ? [] : undefined,
        countryCodes: newFilterType === 'BillingCountry' ? [] : undefined,
      },
    });
  };

  const handleRemoveFilter = (filterId: string) => {
    deleteFilter({ broadcastId, filterId });
  };

  const handleUpdateFilter = (filter: BroadcastFilter) => {
    if (!filter.id) return;
    updateFilter({
      broadcastId,
      filterId: filter.id,
      data: {
        ids: filter.ids,
        productIds: filter.productIds,
        countryCodes: filter.countryCodes,
      },
    });
  };

  const hasAllFilter = filters.some((f) => f.filterType === 'All');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Recipient Filters
          {isBusy && <Loader2 className="h-4 w-4 animate-spin" />}
        </CardTitle>
        <CardDescription>
          Define which recipients should receive this broadcast. Multiple filters are combined with AND logic.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {filters.length === 0 ? (
          <div className="text-center text-muted-foreground py-4 border border-dashed rounded-lg">
            No filters configured. Add a filter to target specific recipients.
          </div>
        ) : (
          <div className="space-y-3">
            {filters.map((filter) => (
              <FilterEditor
                key={filter.id}
                filter={filter}
                onUpdate={handleUpdateFilter}
                onRemove={() => filter.id && handleRemoveFilter(filter.id)}
                disabled={disabled || isBusy}
                products={products}
              />
            ))}
          </div>
        )}

        {!disabled && !hasAllFilter && (
          <div className="flex gap-2 pt-2">
            <Select
              value={newFilterType}
              onValueChange={(value) => setNewFilterType(value as BroadcastFilterType)}
              disabled={isBusy}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select filter type" />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(FILTER_TYPE_LABELS) as BroadcastFilterType[]).map((type) => (
                  <SelectItem key={type} value={type}>
                    {FILTER_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleAddFilter} disabled={!newFilterType || isBusy}>
              <Plus className="h-4 w-4 mr-2" />
              Add Filter
            </Button>
          </div>
        )}

        {hasAllFilter && !disabled && (
          <p className="text-sm text-muted-foreground">
            "All Recipients" filter is active. Remove it to add other filters.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface FilterEditorProps {
  filter: BroadcastFilter;
  onUpdate: (filter: BroadcastFilter) => void;
  onRemove: () => void;
  disabled: boolean;
  products: Array<{ productId?: string }>;
}

function FilterEditor({ filter, onUpdate, onRemove, disabled, products }: FilterEditorProps) {
  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Badge variant="secondary">{filter.filterType && FILTER_TYPE_LABELS[filter.filterType]}</Badge>
          <p className="text-sm text-muted-foreground mt-1">
            {filter.filterType && FILTER_TYPE_DESCRIPTIONS[filter.filterType]}
          </p>
        </div>
        {!disabled && (
          <Button variant="ghost" size="icon" onClick={onRemove}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>

      {filter.filterType === 'Ids' && (
        <IdsFilterEditor filter={filter} onUpdate={onUpdate} disabled={disabled} />
      )}

      {filter.filterType === 'ProductRegistration' && (
        <ProductFilterEditor
          filter={filter}
          onUpdate={onUpdate}
          disabled={disabled}
          products={products}
        />
      )}

      {filter.filterType === 'PaidSubscription' && (
        <PaidSubscriptionFilterEditor
          filter={filter}
          onUpdate={onUpdate}
          disabled={disabled}
          products={products}
        />
      )}

      {filter.filterType === 'BillingCountry' && (
        <BillingCountryFilterEditor filter={filter} onUpdate={onUpdate} disabled={disabled} />
      )}
    </div>
  );
}

interface IdsFilterEditorProps {
  filter: BroadcastFilter;
  onUpdate: (filter: BroadcastFilter) => void;
  disabled: boolean;
}

function IdsFilterEditor({ filter, onUpdate, disabled }: IdsFilterEditorProps) {
  const [inputValue, setInputValue] = useState('');
  const ids = filter.ids ?? [];

  const handleAddId = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !ids.includes(trimmed)) {
      onUpdate({ ...filter, ids: [...ids, trimmed] });
      setInputValue('');
    }
  };

  const handleRemoveId = (id: string) => {
    onUpdate({ ...filter, ids: ids.filter((i) => i !== id) });
  };

  return (
    <div className="space-y-2">
      <Label>User/Organization IDs</Label>
      <div className="flex gap-2">
        <Input
          placeholder="Enter ID (GUID)"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddId())}
          disabled={disabled}
        />
        <Button onClick={handleAddId} disabled={disabled || !inputValue.trim()}>
          Add
        </Button>
      </div>
      {ids.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {ids.map((id) => (
            <Badge key={id} variant="outline" className="font-mono text-xs">
              {id}
              {!disabled && (
                <button onClick={() => handleRemoveId(id)} className="ml-1 hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

interface ProductFilterEditorProps {
  filter: BroadcastFilter;
  onUpdate: (filter: BroadcastFilter) => void;
  disabled: boolean;
  products: Array<{ productId?: string }>;
}

function ProductFilterEditor({ filter, onUpdate, disabled, products }: ProductFilterEditorProps) {
  const productIds = filter.productIds ?? [];

  const handleToggleProduct = (productId: string) => {
    const updated = productIds.includes(productId)
      ? productIds.filter((p) => p !== productId)
      : [...productIds, productId];
    onUpdate({ ...filter, productIds: updated });
  };

  return (
    <div className="space-y-2">
      <Label>Products</Label>
      <div className="flex flex-wrap gap-2">
        {products.map((product) => (
          <Badge
            key={product.productId}
            variant={productIds.includes(product.productId!) ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => !disabled && handleToggleProduct(product.productId!)}
          >
            {product.productId}
          </Badge>
        ))}
        {products.length === 0 && (
          <span className="text-sm text-muted-foreground">No products available</span>
        )}
      </div>
    </div>
  );
}

interface PaidSubscriptionFilterEditorProps {
  filter: BroadcastFilter;
  onUpdate: (filter: BroadcastFilter) => void;
  disabled: boolean;
  products: Array<{ productId?: string }>;
}

function PaidSubscriptionFilterEditor({
  filter,
  onUpdate,
  disabled,
  products,
}: PaidSubscriptionFilterEditorProps) {
  const productIds = filter.productIds ?? [];

  const handleToggleProduct = (productId: string) => {
    const updated = productIds.includes(productId)
      ? productIds.filter((p) => p !== productId)
      : [...productIds, productId];
    onUpdate({ ...filter, productIds: updated.length > 0 ? updated : undefined });
  };

  return (
    <div className="space-y-2">
      <Label>Products (optional - leave empty for any subscription)</Label>
      <div className="flex flex-wrap gap-2">
        {products.map((product) => (
          <Badge
            key={product.productId}
            variant={productIds.includes(product.productId!) ? 'default' : 'outline'}
            className="cursor-pointer"
            onClick={() => !disabled && handleToggleProduct(product.productId!)}
          >
            {product.productId}
          </Badge>
        ))}
        {products.length === 0 && (
          <span className="text-sm text-muted-foreground">No products available</span>
        )}
      </div>
      {productIds.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Currently targeting users with any active subscription
        </p>
      )}
    </div>
  );
}

interface BillingCountryFilterEditorProps {
  filter: BroadcastFilter;
  onUpdate: (filter: BroadcastFilter) => void;
  disabled: boolean;
}

function BillingCountryFilterEditor({
  filter,
  onUpdate,
  disabled,
}: BillingCountryFilterEditorProps) {
  const [inputValue, setInputValue] = useState('');
  const countryCodes = filter.countryCodes ?? [];

  const handleAddCountry = () => {
    const trimmed = inputValue.trim().toUpperCase();
    if (trimmed && trimmed.length === 2 && !countryCodes.includes(trimmed)) {
      onUpdate({ ...filter, countryCodes: [...countryCodes, trimmed] });
      setInputValue('');
    }
  };

  const handleRemoveCountry = (code: string) => {
    onUpdate({ ...filter, countryCodes: countryCodes.filter((c) => c !== code) });
  };

  return (
    <div className="space-y-2">
      <Label>Country Codes (ISO 2-letter)</Label>
      <div className="flex gap-2">
        <Input
          placeholder="e.g. US, CA, GB"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCountry())}
          disabled={disabled}
          maxLength={2}
          className="w-24"
        />
        <Button onClick={handleAddCountry} disabled={disabled || inputValue.trim().length !== 2}>
          Add
        </Button>
      </div>
      {countryCodes.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {countryCodes.map((code) => (
            <Badge key={code} variant="outline">
              {code}
              {!disabled && (
                <button
                  onClick={() => handleRemoveCountry(code)}
                  className="ml-1 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
